import { useCallback, useEffect, useMemo, useState } from "react";
import { rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Link2, Pencil, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { embedUrl } from "@/lib/media";
import { UploadBtn } from "@/components/admin/SermonForm";
import { field, card } from "@/components/admin/SettingsPanel";
import { GalleryForm } from "@/components/admin/GalleryForm";
import { DepartmentsManager } from "@/components/admin/DepartmentsManager";
import { useSite } from "@/context/SiteContext";
import { SortableArea, mergeOrder } from "@/components/admin/Sortable";
import { Thumb } from "@/pages/Events";

const pendingToast = () => toast("Sent for approval — it will appear once a Super Admin approves it");

const Tile = ({ item, i, canDrag, onEdit, onDelete, deptName }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canDrag });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`admin-gallery-item-${i}`} className={`overflow-hidden rounded-2xl border bg-[#131C2E] ${isDragging ? "z-10 border-[#D4AF37] shadow-xl" : "border-[#F4EEE2]/10"}`}>
      <div className="relative aspect-[4/3] bg-black">
        <Thumb item={item} />
        {item.kind === "video" && <Play className="absolute left-3 top-3 h-5 w-5 rounded-full bg-[#D4AF37] p-1 text-[#0B111E]" />}
        {canDrag && (
          <button type="button" data-testid={`admin-gallery-drag-${i}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="absolute right-2 top-2 cursor-grab touch-none rounded-lg bg-white/90 p-1.5 text-[#0B111E] active:cursor-grabbing">
            <GripVertical className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 p-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-ui text-sm font-semibold text-[#F4EEE2]">{item.title_en || "Untitled"}</p>
          <p className="truncate text-xs text-[#B7BFCC]">{deptName || "No department"}{item.album ? ` · ${item.album}` : ""}</p>
        </div>
        <button type="button" data-testid={`admin-gallery-edit-${i}`} onClick={onEdit} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
        <button type="button" data-testid={`admin-gallery-delete-${i}`} onClick={onDelete} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
};

export const GalleryPanel = () => {
  const { settings } = useSite();
  const deps = Array.isArray(settings?.departments) ? settings.departments : [];
  const deptName = (slug) => deps.find((d) => d.slug === slug)?.en || "";
  const [items, setItems] = useState([]);
  const [dept, setDept] = useState("");
  const [album, setAlbum] = useState("");
  const [link, setLink] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/gallery").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  const albums = useMemo(() => [...new Set(items.filter((x) => !dept || x.department === dept).map((x) => x.album).filter(Boolean))], [items, dept]);
  const counts = useMemo(() => items.reduce((m, x) => ({ ...m, [x.department]: (m[x.department] || 0) + 1 }), {}), [items]);
  const shown = filter === "all" ? items : items.filter((x) => (filter === "none" ? !deptName(x.department) : x.department === filter));

  const add = async (body) => {
    try {
      const { data } = await api.post("/gallery", { title_en: "", title_mh: "", date: "", department: dept, ...body, album: body.album ?? album.trim() });
      if (data?.pending) pendingToast();
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };
  const needDept = () => { if (!dept && deps.length) { toast.error("Choose a department first"); return true; } return false; };
  const addLink = async () => {
    if (needDept()) return;
    if (!embedUrl(link)) return toast.error("Paste a YouTube or Facebook video link");
    if (await add({ kind: "video", url: link.trim() })) { setLink(""); toast.success("Video added"); }
  };
  const del = async (x) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const { data } = await api.delete(`/gallery/${x.id}`);
      if (data?.pending) pendingToast(); else toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onReorder = async (ids) => {
    const prev = items;
    const next = mergeOrder(items, ids);
    setItems(next);
    try { await api.put("/gallery/order", { ids: next.map((x) => x.id) }); toast.success("Order saved"); }
    catch (e) { setItems(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="gallery-panel" className="grid gap-4">
      <DepartmentsManager key={deps.map((d) => d.slug).join()} counts={counts} />
      <div className={card}>
        <h3 className="font-display text-2xl text-[#F4EEE2]">Add photos & videos</h3>
        <p className="mt-1 text-sm text-[#B7BFCC]">1. Choose the department. 2. Optional: type the program or event name (for example “Youth Night 2026”). 3. Upload. You can pick many files at once. Videos up to 500MB.</p>
        <select data-testid="gallery-dept-select" value={dept} onChange={(e) => setDept(e.target.value)} className={`${field} mt-4`}>
          <option value="">{deps.length ? "Choose a department…" : "No department"}</option>
          {deps.map((d) => <option key={d.slug} value={d.slug}>{d.en}</option>)}
        </select>
        <input data-testid="gallery-album-input" list="gallery-albums" value={album} onChange={(e) => setAlbum(e.target.value)} placeholder="Program / event name (optional)" className={`${field} mt-3`} />
        <datalist id="gallery-albums">{albums.map((a) => <option key={a} value={a} />)}</datalist>
        <div className={`mt-3 flex flex-wrap gap-3 ${!dept && deps.length ? "pointer-events-none opacity-50" : ""}`} data-testid="gallery-upload-wrap">
          <UploadBtn multiple accept="image/*,video/mp4,video/quicktime,video/webm" testid="gallery-upload" label="Upload photos / videos" onDone={(url, f) => add({ kind: f.type.startsWith("video/") ? "video" : "photo", url })} />
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="gallery-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Or paste a YouTube / Facebook video link" className={field} />
          <button type="button" data-testid="gallery-link-add-btn" onClick={addLink} className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E]"><Link2 className="h-4 w-4" /> Add link</button>
        </div>
      </div>
      <div className={card}>
        <div className="flex flex-wrap items-center gap-2">
          {[["all", `All (${items.length})`], ...deps.map((d) => [d.slug, `${d.en} (${counts[d.slug] || 0})`]), ...(items.some((x) => !deptName(x.department)) ? [["none", "No department"]] : [])].map(([k, l], i) => (
            <button type="button" key={k} data-testid={`admin-gallery-filter-${i}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-1.5 font-ui text-xs ${filter === k ? "bg-[#D4AF37] font-semibold text-[#0B111E]" : "border border-[#F4EEE2]/15 text-[#F4EEE2]"}`}>{l}</button>
          ))}
        </div>
        <p className="mt-3 text-xs text-[#9AA4B4]">Drag the handle on any item to change the order. The first item shows first on the website{filter === "all" ? "" : " and on this department's page"}.</p>
        {shown.length ? (
          <SortableArea ids={shown.map((x) => x.id)} onReorder={onReorder} strategy={rectSortingStrategy}>
              <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {shown.map((x, i) => <Tile key={x.id} item={x} i={i} deptName={deptName(x.department)} canDrag onEdit={() => setEditing(x)} onDelete={() => del(x)} />)}
              </ul>
          </SortableArea>
        ) : (
          <p data-testid="admin-gallery-empty" className="py-10 text-center text-sm text-[#B7BFCC]">Nothing here yet. Upload your first photos above.</p>
        )}
      </div>
      {editing && <GalleryForm item={editing} albums={albums} deps={deps} onClose={() => setEditing(null)} onSaved={(pending) => { if (pending) pendingToast(); else toast.success("Saved"); setEditing(null); load(); }} />}
    </div>
  );
};
